import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";

const initialState = {
	fundingSources: [],
	loading: false,
	error: null,
	total: 0,
};

const FETCH_FUNDING_SOURCES_REQUEST = "FETCH_FUNDING_SOURCES_REQUEST";
const FETCH_FUNDING_SOURCES_SUCCESS = "FETCH_FUNDING_SOURCES_SUCCESS";
const FETCH_FUNDING_SOURCES_FAILURE = "FETCH_FUNDING_SOURCES_FAILURE";
const CREATE_FUNDING_SOURCE_REQUEST = "CREATE_FUNDING_SOURCE_REQUEST";
const CREATE_FUNDING_SOURCE_SUCCESS = "CREATE_FUNDING_SOURCE_SUCCESS";
const CREATE_FUNDING_SOURCE_FAILURE = "CREATE_FUNDING_SOURCE_FAILURE";
const UPDATE_FUNDING_SOURCE_REQUEST = "UPDATE_FUNDING_SOURCE_REQUEST";
const UPDATE_FUNDING_SOURCE_SUCCESS = "UPDATE_FUNDING_SOURCE_SUCCESS";
const UPDATE_FUNDING_SOURCE_FAILURE = "UPDATE_FUNDING_SOURCE_FAILURE";
const DELETE_FUNDING_SOURCE_REQUEST = "DELETE_FUNDING_SOURCE_REQUEST";
const DELETE_FUNDING_SOURCE_SUCCESS = "DELETE_FUNDING_SOURCE_SUCCESS";
const DELETE_FUNDING_SOURCE_FAILURE = "DELETE_FUNDING_SOURCE_FAILURE";

export const getFundingSources =
	(
		page = 0,
		pageSize = 20,
		sortField = "id",
		sortDirection = "asc",
        search = "",
		filters = {},
		userAgency,
	) =>
	async (dispatch) => {
		const service = getService(serviceNames.perFundingSources);
		const query = {
			$limit: pageSize,
			$skip: page * pageSize,
			$sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
			isDeleted: false,
			agencyId: userAgency,
		};

		const textFields = ["title", "source", "administrator"];

        if (search) {
            query.$or = textFields.map((field) => ({
                [field]: { $like: `%${search}%` },
            }));
        }

		textFields.forEach((field) => {
			const value = filters[field];
			if (value) {
				query[field] = filters.exactMatch
					? value
					: { $like: `%${value}%` };
			}
		});

		const getQueryValue = (input) => {
			if (Array.isArray(input)) {
				const values = input.map((i) => i.value).filter(Boolean);
				return values.length > 0 ? { $in: values } : undefined;
			} else if (input?.value) {
				return input.value;
			}
			return undefined;
		};

		const multiSelectFields = ["status", "source", "administrator"];

		multiSelectFields.forEach((field) => {
			const value = getQueryValue(filters[field]);
			if (value !== undefined) {
				query[field] = value;
			}
		});

		if (filters.from) {
			query.startDate = { $gte: filters.from };
		}
		if (filters.to) {
			query.endDate = { $lte: filters.to };
		}

		if (filters.filterField?.value && filters.filterValue) {
			const field = filters.filterField.value;
			query[field] = filters.exactMatch
				? filters.filterValue
				: { $like: `%${filters.filterValue}%` };
		}

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: FETCH_FUNDING_SOURCES_REQUEST,
				success: FETCH_FUNDING_SOURCES_SUCCESS,
				failure: FETCH_FUNDING_SOURCES_FAILURE,
			},
			apiCall: async () => await service.find({ query }),
			transformResult: (res) => {
				return {
					fundingSources: res.data,
					total: res.total,
				};
			},
			errorMessagePrefix: "Failed to fetch funding sources",
		});
	};

export const createFundingSource = (fundingSourceData) => async (dispatch) => {
	const service = getService(serviceNames.perFundingSources);
	try {
		// Add duplicate check if needed, similar to employees

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: CREATE_FUNDING_SOURCE_REQUEST,
				success: CREATE_FUNDING_SOURCE_SUCCESS,
				failure: CREATE_FUNDING_SOURCE_FAILURE,
			},
			apiCall: () => service.create(fundingSourceData),
			successMessage: "Funding source created successfully",
			errorMessagePrefix: "Failed to create funding source",
		});
	} catch (err) {
		dispatch({
			type: CREATE_FUNDING_SOURCE_FAILURE,
			payload: err.message,
		});
		return null;
	}
};

export const updateFundingSource =
	(id, fundingSourceData) => async (dispatch) => {
		const service = getService(serviceNames.perFundingSources);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_FUNDING_SOURCE_REQUEST,
				success: UPDATE_FUNDING_SOURCE_SUCCESS,
				failure: UPDATE_FUNDING_SOURCE_FAILURE,
			},
			apiCall: () => service.patch(id, fundingSourceData),
			successMessage: "Funding source updated successfully",
			errorMessagePrefix: "Failed to update funding source",
		});
	};

export const deleteFundingSource = (id, userId) => async (dispatch) => {
	const service = getService(serviceNames.perFundingSources);
	const patchData = {
		isDeleted: true,
		deletedBy: userId,
		deletedAt: new Date().toISOString(),
	};

	return await asyncThunkWrapper({
		dispatch,
		types: {
			request: DELETE_FUNDING_SOURCE_REQUEST,
			success: DELETE_FUNDING_SOURCE_SUCCESS,
			failure: DELETE_FUNDING_SOURCE_FAILURE,
		},
		apiCall: () => service.patch(id, patchData),
		transformResult: () => id,
		successMessage: "Funding source deleted successfully",
		errorMessagePrefix: "Failed to delete funding source",
	});
};

export default function fundingSourcesReducer(state = initialState, action) {
	switch (action.type) {
		case FETCH_FUNDING_SOURCES_REQUEST:
		case CREATE_FUNDING_SOURCE_REQUEST:
		case UPDATE_FUNDING_SOURCE_REQUEST:
		case DELETE_FUNDING_SOURCE_REQUEST:
			return { ...state, loading: true, error: null };
		case FETCH_FUNDING_SOURCES_SUCCESS:
			return {
				...state,
				loading: false,
				fundingSources: action.payload.fundingSources,
				total: action.payload.total,
			};
		case CREATE_FUNDING_SOURCE_SUCCESS:
			return {
				...state,
				loading: false,
				fundingSources: [...state.fundingSources, action.payload],
				total: state.total + 1,
			};
		case UPDATE_FUNDING_SOURCE_SUCCESS:
			return {
				...state,
				loading: false,
				fundingSources: state.fundingSources.map((fs) =>
					fs.id === action.payload.id ? action.payload : fs,
				),
			};
		case DELETE_FUNDING_SOURCE_SUCCESS:
			return {
				...state,
				loading: false,
				fundingSources: state.fundingSources.filter(
					(fs) => fs.id !== action.payload,
				),
				total: state.total - 1,
			};
		case FETCH_FUNDING_SOURCES_FAILURE:
		case CREATE_FUNDING_SOURCE_FAILURE:
		case UPDATE_FUNDING_SOURCE_FAILURE:
		case DELETE_FUNDING_SOURCE_FAILURE:
			return { ...state, loading: false, error: action.payload };
		default:
			return state;
	}
}
